/* eslint-disable max-len */
import React from 'react';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from '@components/Base/Checkbox';
import { CustomColumnDef } from '@components/Base/Table/types';
import User from '@components/Base/UserDetails';
import { convertToTitleCase } from '@components/ProctorNgSessions/SessionTable/helper';
import { Transactions } from '@containers/transactions/types';
import { classNames } from '@utils/classNames';
import { formatDate } from '@utils/dateFormat';
import gripHorizontalIcon from 'app/images/grip-horizontal.svg';

import messages from './messages';
import './styles.css';

const EDIT_ALT = 'edit';
const DASH = '-';

export const createColumns = (): CustomColumnDef<Transactions>[] => [
  {
    id: 'Transaction ID',
    header: () => <FormattedMessage {...messages.transaction_id} />,
    accessorKey: 'id',
    enableSorting: false,
    hidden: false,
    cell: ({ row }) => <div>{row.original.id}</div>,
    size: 200,
    className:
      'text-left text-sm font-medium font-roboto text-gray-900 left-[0px] sticky attendee-column shadow-[inset_-1px_0px_0px_0px] shadow-gray-400/20',
    cellClassName:
      'text-left sticky left-[0px] attendee-column shadow-[inset_-1px_0px_0px_0px] z-[1] shadow-gray-400/20',
  },
  {
    id: 'Drive Enrollment ID',
    header: () => <FormattedMessage {...messages.drive_enrollment_id} />,
    accessorKey: 'drive enrollment id',
    enableSorting: false,
    cell: () => <div>{DASH}</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Candidate Details',
    header: () => <FormattedMessage {...messages.candidate_details} />,
    accessorKey: 'Candidate Details',
    enableSorting: false,
    cell: ({ row }) => (
      <User
        data={{
          name: row.original.created_by_details?.email || 'NA',
        }}
        size="sm"
        hideImage={false}
        additionalContent={
          row.original.created_by_details?.created_at
            ? formatDate(new Date(row.original.created_by_details.created_at), 'withTime')
            : ''
        }
      />
    ),
    size: 260,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'SKU',
    header: () => <FormattedMessage {...messages.sku} />,
    accessorKey: 'SKU',
    enableSorting: false,
    cell: () => <div>{DASH}</div>,
    size: 200,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Transaction Action',
    header: () => <FormattedMessage {...messages.transaction_action} />,
    accessorKey: 'Transaction Action',
    enableSorting: false,
    cell: () => <div>{DASH}</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Transaction Date',
    header: () => <FormattedMessage {...messages.transaction_date} />,
    accessorKey: 'Transaction Date',
    enableSorting: false,
    cell: ({ row }) => (
      <div>{row.original?.created_at ? formatDate(new Date(row.original.created_at), 'withTime') : ''}</div>
    ),
    size: 250,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Status',
    header: () => <FormattedMessage {...messages.status} />,
    accessorKey: 'Status',
    enableSorting: false,
    cell: ({ row }) => <div>{row.original.status}</div>,
    size: 200,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'SKU Value',
    header: () => <FormattedMessage {...messages.sku_value} />,
    accessorKey: 'SKU Value',
    enableSorting: false,
    cell: () => <div>{DASH}</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Amount Paid',
    header: () => <FormattedMessage {...messages.amount_paid} />,
    accessorKey: 'Amount Paid',
    enableSorting: false,
    cell: () => <div>{DASH}</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Discount Applied',
    header: () => <FormattedMessage {...messages.discount_applied} />,
    accessorKey: 'Discount Applied',
    enableSorting: false,
    cell: () => <div>{DASH}</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Voucher Code',
    header: () => <FormattedMessage {...messages.voucher_code} />,
    accessorKey: 'Voucher Code',
    enableSorting: false,
    cell: () => <div>{DASH}</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Country of Residence',
    header: () => <FormattedMessage {...messages.country_of_residence} />,
    accessorKey: 'residence',
    enableSorting: false,
    cell: () => <div>{DASH}</div>,
    size: 210,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Invoice ID',
    header: () => <FormattedMessage {...messages.invoice_id} />,
    accessorKey: 'Invoice ID',
    enableSorting: false,
    cell: () => <div>{DASH}</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'menu',
    enableSorting: false,
    accessorKey: 'menu',
    size: 48,
    className: 'text-left right-0 sticky shadow-[inset_0px_0px_0px_-1px] shadow-gray-400/20',
    cellClassName: 'text-center right-0 sticky z-[1] shadow-[inset_0px_0px_0px_-1px] shadow-gray-400/20',
    header: ({ table }) => (
      <div>
        <Popover className="flex justify-end items-center pr-3">
          <PopoverButton className={classNames('focus:outline-none')}>
            <PlusCircleIcon
              className={classNames('text-black h-5 w-5 focus:outline-none')}
              aria-hidden="true"
              data-testid="hide-column"
            />
          </PopoverButton>
          <Transition>
            <PopoverPanel>
              <div
                className="bg-white border-solid border-[1px] popover-width font-normal shadow-sm max-h-[480px] overflow-y-auto rounded-md px-4 absolute right-0 top-12"
                data-testid="column-model"
              >
                {table.getAllLeafColumns().map((column) => {
                  if (column.id === 'menu') {
                    return null;
                  }
                  return (
                    <div key={column.id} className="h-[40px] w-full flex items-center">
                      <span className="pr-2 w-6 h-6">
                        <img src={gripHorizontalIcon} alt={EDIT_ALT} className="w-full h-full" />
                      </span>
                      <Checkbox
                        label={
                          typeof column.columnDef.header === 'string'
                            ? column.columnDef.header
                            : convertToTitleCase(column.id)
                        }
                        id={column.id}
                        size="md"
                        checked={column.getIsVisible()}
                        onChange={column.getToggleVisibilityHandler()}
                        disabled={false}
                      />
                    </div>
                  );
                })}
              </div>
            </PopoverPanel>
          </Transition>
        </Popover>
      </div>
    ),
  },
];
